import { useState, useEffect, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { fmtTimestamp } from "@/lib/format";
import { RefreshCw, Plug, Link2, CalendarCheck2 } from "lucide-react";
import { toast } from "sonner";

const RecRow = ({ children }) => <div className="rounded-lg border border-border p-2 text-sm flex items-center justify-between gap-2 flex-wrap">{children}</div>;

const StatusBadge = ({ status }) => {
  const map = { Pianificata: "bg-emerald-100 text-emerald-800", Svolta: "bg-blue-100 text-blue-800", Annullata: "bg-rose-100 text-rose-700" };
  return <span data-testid="gcal-status-badge" className={`ml-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${map[status] || "bg-muted text-muted-foreground"}`}>{status}</span>;
};

const ConfField = ({ label, v }) => <div className="flex justify-between gap-2 py-0.5"><span className="text-xs text-muted-foreground">{label}</span><span className="text-xs font-medium text-right">{v || "—"}</span></div>;
const fmtDT = (iso) => { if (!iso) return ""; try { return new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; } };

export const GoogleCalendarPanel = () => {
  const [st, setSt] = useState(null);
  const [cals, setCals] = useState([]);
  const [recOpen, setRecOpen] = useState(false);
  const [rec, setRec] = useState(null);
  const [range, setRange] = useState({ from: "", to: "" });
  const [busy, setBusy] = useState(false);
  const [confSid, setConfSid] = useState(null);
  const [confData, setConfData] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [pendingCal, setPendingCal] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(() => {
    api.get("/calendar/status")
      .then((r) => { setSt(r.data); setErr(null); })
      .catch((e) => { setErr(e?.response?.status === 403 ? "unavailable" : "error"); });
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("calendar");
    if (p === "connected") toast.success("Google Calendar connesso");
    else if (p) toast.error("Connessione Google non riuscita");
    if (p) window.history.replaceState({}, "", window.location.pathname);
  }, []);

  useEffect(() => { if (st?.connected) api.get("/calendar/calendars").then((r) => setCals(r.data)).catch(() => {}); }, [st?.connected]);

  const connect = async () => { try { const r = await api.get("/oauth/calendar/login"); window.location.href = r.data.authorization_url; } catch (e) { toast.error(apiErr(e)); } };
  const doSelectCal = async (v) => { const c = cals.find((x) => x.id === v); try { const r = await api.post("/calendar/select-calendar", { calendar_id: v, calendar_name: c?.name || "" }); setSt(r.data); setPendingCal(null); toast.success("Calendario di destinazione aggiornato"); } catch (e) { toast.error(apiErr(e)); } };
  const selectCal = async (v) => {
    if (!v || v === st?.calendar_id) return; // same destination: idempotent, no request, no mutation
    // Authoritative CURRENT state right before a meaningful switch (never trust a stale boolean
    // loaded earlier while events may have been created/cancelled elsewhere). Fail closed on error.
    let cur;
    try { cur = (await api.get("/calendar/status")).data; setSt(cur); }
    catch (e) { toast.error("Impossibile verificare lo stato del calendario. Riprova."); return; }
    if (cur.has_synced_links) setPendingCal(v); else doSelectCal(v);
  };
  const toggleSync = async (enabled) => { try { const r = await api.post("/calendar/sync-toggle", { enabled }); setSt(r.data); } catch (e) { toast.error(apiErr(e)); } };
  const syncNow = async () => { setBusy(true); try { const r = await api.post("/calendar/sync-now"); toast.success(`Sync completata · creati ${r.data.created || 0}, aggiornati ${r.data.synced || 0}`); load(); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); } };
  const disconnect = async () => { try { const r = await api.post("/calendar/disconnect"); toast.success(r.data.message || "Disconnesso"); setCals([]); load(); } catch (e) { toast.error(apiErr(e)); } };
  const clearError = async () => { try { const r = await api.post("/calendar/clear-error"); setSt(r.data); toast.success("Stato di errore azzerato"); } catch (e) { toast.error(apiErr(e)); } };
  const localReset = async () => { try { const r = await api.post("/calendar/local-reset", { confirm: "RESET_LOCAL_CALENDAR" }); setResetOpen(false); setCals([]); toast.success(r.data.message || "Connessione Calendar locale azzerata. Google Calendar non è stato modificato."); load(); } catch (e) { toast.error(apiErr(e)); } };
  const runRec = async () => { setBusy(true); try { const q = []; if (range.from) q.push(`from_date=${range.from}`); if (range.to) q.push(`to_date=${range.to}`); const r = await api.get(`/calendar/reconcile${q.length ? `?${q.join("&")}` : ""}`); setRec(r.data); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); } };
  const openRec = () => { setRec(null); setRecOpen(true); runRec(); };
  const createEvt = async (sid) => { try { const r = await api.post(`/calendar/sync-session/${sid}`); toast.success(r.data?.status === "synced" ? "Evento Calendar aggiornato" : "Evento Calendar creato"); runRec(); } catch (e) { toast.error(apiErr(e)); } };
  const linkEvt = async (sid, eid) => { try { await api.post(`/calendar/link/${sid}`, { event_id: eid }); toast.success("Evento collegato"); runRec(); } catch (e) { toast.error(apiErr(e)); } };
  const openConflict = async (sid) => { setConfSid(sid); setConfData(null); try { const r = await api.get(`/calendar/conflict/${sid}`); setConfData(r.data); } catch (e) { toast.error(apiErr(e)); setConfSid(null); } };
  const resolveKeepK9 = async () => { try { await api.post(`/calendar/resolve/${confSid}/keep-k9`); toast.success("Conflitto risolto · dati K9 Manager mantenuti"); setConfSid(null); runRec(); } catch (e) { toast.error(apiErr(e)); } };
  const resolveUseCalendar = async () => { try { await api.post(`/calendar/resolve/${confSid}/use-calendar`); toast.success("Aggiornati data/ora/luogo da Calendar"); setConfSid(null); runRec(); } catch (e) { toast.error(apiErr(e)); } };

  if (err === "unavailable") return <p data-testid="gcal-unavailable" className="text-sm text-muted-foreground">Google Calendar non ancora disponibile per questo account.</p>;
  if (!st) {
    if (err) return <p data-testid="gcal-load-error" className="text-sm text-muted-foreground">Impossibile caricare lo stato di Google Calendar. Riprova più tardi.</p>;
    return <p className="text-sm text-muted-foreground">Caricamento…</p>;
  }

  if (!st.configured) {
    if (st.incomplete) {
      return (
        <div data-testid="gcal-incomplete" className="text-sm">
          <span className="inline-flex items-center gap-2 font-semibold"><span className="h-2 w-2 rounded-full bg-amber-500" />Configurazione Google Calendar incompleta</span>
          <p className="mt-2 text-muted-foreground">Le credenziali OAuth sono presenti ma manca (o non è valida) la chiave di cifratura token <b>K9_TOKEN_ENCRYPTION_KEY</b> nell'ambiente backend. La connessione resta disabilitata finché la chiave non è configurata.</p>
        </div>
      );
    }
    return (
      <div data-testid="gcal-not-configured" className="text-sm">
        <span className="inline-flex items-center gap-2 font-semibold"><span className="h-2 w-2 rounded-full bg-muted-foreground/60" />Stato: Non configurato</span>
        <p className="mt-2 text-muted-foreground">Configura le credenziali Google OAuth e la chiave di cifratura token nell'ambiente backend (GOOGLE_CLIENT_ID, GOOGLE_CLIENT_SECRET, GOOGLE_REDIRECT_URI, K9_TOKEN_ENCRYPTION_KEY) per abilitare la connessione.</p>
      </div>
    );
  }

  if (!st.connected) {
    return (
      <div data-testid="gcal-disconnected" className="text-sm space-y-3">
        <span className="inline-flex items-center gap-2 font-semibold"><span className="h-2 w-2 rounded-full bg-amber-500" />Stato: Non connesso</span>
        <p className="text-muted-foreground">Collega un account Google. La connessione non scrive nulla sul calendario finché non abiliti la sincronizzazione.</p>
        <Button data-testid="gcal-connect" onClick={connect} className="rounded-full"><Plug className="h-4 w-4 mr-1" />Connetti Google Calendar</Button>
      </div>
    );
  }

  return (
    <div data-testid="gcal-connected" className="text-sm space-y-4">
      <div className="grid sm:grid-cols-2 gap-3">
        <div><div className="text-xs text-muted-foreground">Account Google</div><div className="font-medium">{st.email || "—"}</div></div>
        <div><div className="text-xs text-muted-foreground">Ultima sincronizzazione</div><div className="font-medium">{st.last_sync_at ? `${fmtTimestamp(st.last_sync_at)} · ${st.last_sync_status || ""}` : "Mai"}</div></div>
      </div>

      {st.last_error && (
        <div data-testid="gcal-error-banner" className="rounded-xl border border-amber-300 bg-amber-50 p-3">
          <div className="font-semibold text-amber-800 text-sm">Sincronizzazione interrotta</div>
          <p className="text-xs text-amber-700 mt-1">{st.last_error}</p>
          <div className="flex gap-2 mt-2 flex-wrap">
            <Button size="sm" variant="outline" data-testid="gcal-reconnect" onClick={connect} className="rounded-full"><Plug className="h-4 w-4 mr-1" />Ricollega</Button>
            <Button size="sm" variant="outline" data-testid="gcal-clear-error" onClick={clearError} className="rounded-full">Pulisci errore</Button>
          </div>
        </div>
      )}

      <div>
        <Label className="text-xs">Calendario di destinazione</Label>
        <Select value={st.calendar_id || ""} onValueChange={selectCal}>
          <SelectTrigger data-testid="gcal-calendar-select" className="mt-1"><SelectValue placeholder="Seleziona un calendario…" /></SelectTrigger>
          <SelectContent>
            {cals.filter((c) => c.can_edit).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.primary ? " (principale)" : ""}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center justify-between rounded-xl border border-border p-3">
        <div>
          <div className="font-semibold">Sincronizzazione automatica</div>
          <div className="text-xs text-muted-foreground">Quando attiva, le prestazioni pianificate vengono rispecchiate nel calendario.</div>
        </div>
        <Switch data-testid="gcal-sync-toggle" checked={!!st.sync_enabled} onCheckedChange={toggleSync} disabled={!st.calendar_id} />
      </div>

      <div className="flex gap-2 flex-wrap">
        <Button size="sm" variant="outline" data-testid="gcal-reconcile" onClick={openRec} className="rounded-full"><CalendarCheck2 className="h-4 w-4 mr-1" />Riconcilia calendario</Button>
        <Button size="sm" data-testid="gcal-sync-now" onClick={syncNow} disabled={busy || !st.sync_enabled} className="rounded-full"><RefreshCw className="h-4 w-4 mr-1" />Sincronizza ora</Button>
        <Button size="sm" variant="outline" data-testid="gcal-disconnect" onClick={disconnect} className="rounded-full text-destructive">Disconnetti</Button>
      </div>

      <div className="mt-3 rounded-xl border border-dashed border-border p-3">
        <div className="text-xs font-semibold text-muted-foreground mb-1">Manutenzione</div>
        <div className="text-xs text-muted-foreground mb-2">Azzera solo lo stato di connessione memorizzato in K9 Manager. Non tocca gli eventi su Google né revoca l'accesso.</div>
        <Button size="sm" variant="outline" data-testid="gcal-local-reset" onClick={() => setResetOpen(true)} className="rounded-full">Azzera connessione locale</Button>
      </div>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="max-w-md" data-testid="gcal-local-reset-modal">
          <DialogHeader>
            <DialogTitle className="font-head">Azzera connessione locale</DialogTitle>
            <DialogDescription>Questa operazione rimuove da K9 Manager la connessione Google Calendar locale e i collegamenti tecnici agli eventi. Non modifica né elimina gli eventi su Google Calendar e non revoca l'accesso dell'account Google.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" className="rounded-full" data-testid="gcal-local-reset-cancel" onClick={() => setResetOpen(false)}>Annulla</Button>
            <Button size="sm" className="rounded-full text-destructive" data-testid="gcal-local-reset-confirm" onClick={localReset}>Azzera connessione locale</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pendingCal} onOpenChange={(o) => { if (!o) setPendingCal(null); }}>
        <DialogContent className="max-w-md" data-testid="gcal-switch-calendar-modal">
          <DialogHeader>
            <DialogTitle className="font-head">Cambia calendario di destinazione</DialogTitle>
            <DialogDescription>Le prestazioni già sincronizzate resteranno collegate agli eventi nel calendario precedente. Le nuove sincronizzazioni useranno il nuovo calendario. Nessun evento esistente verrà spostato o eliminato.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" className="rounded-full" data-testid="gcal-switch-cancel" onClick={() => setPendingCal(null)}>Annulla</Button>
            <Button size="sm" className="rounded-full" data-testid="gcal-switch-confirm" onClick={() => doSelectCal(pendingCal)}>Cambia calendario</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={recOpen} onOpenChange={setRecOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-head">Riconciliazione (sola lettura)</DialogTitle>
            <DialogDescription>Nessuna modifica viene applicata automaticamente. Le azioni sono esplicite.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 items-end flex-wrap mb-3">
            <div><Label className="text-xs">Da</Label><Input type="date" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} className="h-9" /></div>
            <div><Label className="text-xs">A</Label><Input type="date" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} className="h-9" /></div>
            <Button size="sm" onClick={runRec} disabled={busy} className="rounded-full">Aggiorna</Button>
          </div>
          {!rec ? <p className="text-sm text-muted-foreground">Caricamento…</p> : (() => {
            const conflicts = (rec.linked || []).filter((x) => x.sync_status === "conflict");
            return (
            <div className="space-y-4 text-sm">
              <div data-testid="gcal-rec-counts" className="text-xs text-muted-foreground">Solo K9 da sincronizzare: {rec.counts.k9_only} · Storico K9: {rec.counts.history ?? 0} · Conflitti: {conflicts.length} · Collegati: {rec.counts.linked} · Solo Calendar: {rec.counts.calendar_only} · Possibili match: {rec.counts.possible_match} · Possibili duplicati: {rec.counts.possible_duplicate}</div>

              {conflicts.length > 0 && (
                <div><div className="font-semibold text-[#C2410C] mb-1">Conflitti da risolvere</div>
                  {conflicts.map((s) => (
                    <RecRow key={s.session_id}><span>{s.title} · {s.date} {s.start_time}</span>
                      <Button size="sm" variant="outline" data-testid={`gcal-resolve-conflict-${s.session_id}`} className="rounded-full" onClick={() => openConflict(s.session_id)}>Risolvi conflitto</Button>
                    </RecRow>
                  ))}
                </div>
              )}

              {rec.possible_duplicate.length > 0 && (
                <div><div className="font-semibold text-[#C2410C] mb-1">Possibili duplicati (solo segnalazione)</div>
                  {rec.possible_duplicate.map((d, i) => <RecRow key={i}><span>{d.title} · {d.start}</span><span className="text-xs text-muted-foreground">{d.event_ids.length} eventi</span></RecRow>)}
                </div>
              )}

              <div><div className="font-semibold mb-1">Solo K9 — da sincronizzare</div>
                {rec.k9_only.length === 0 ? <p className="text-muted-foreground text-xs">Nessuno.</p> : rec.k9_only.map((s) => (
                  <RecRow key={s.session_id}><span>{s.title} · {s.date} {s.start_time} <StatusBadge status={s.status} /></span>
                    <Button size="sm" variant="outline" data-testid={`gcal-create-event-${s.session_id}`} className="rounded-full" onClick={() => createEvt(s.session_id)} disabled={!st.calendar_id}>Crea evento Calendar</Button>
                  </RecRow>
                ))}
              </div>

              <div><div className="font-semibold mb-1">Possibili match (collega manualmente)</div>
                {rec.possible_match.length === 0 ? <p className="text-muted-foreground text-xs">Nessuno.</p> : rec.possible_match.map((s) => (
                  <RecRow key={s.session_id}><span>{s.title} · {s.date} {s.start_time} ↔ «{s.event_title}»</span>
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => linkEvt(s.session_id, s.event_id)}><Link2 className="h-4 w-4 mr-1" />Collega evento esistente</Button>
                  </RecRow>
                ))}
              </div>

              <div><div className="font-semibold mb-1">Storico K9 non sincronizzabile</div>
                {(rec.history || []).length === 0 ? <p className="text-muted-foreground text-xs">Nessuno.</p> : rec.history.map((s) => (
                  <RecRow key={s.session_id}><span data-testid={`gcal-history-${s.session_id}`}>{s.title} · {s.date} {s.start_time} <StatusBadge status={s.status} /></span><span className="text-xs text-muted-foreground">Solo storico</span></RecRow>
                ))}
              </div>

              <div><div className="font-semibold mb-1">Solo Calendar (non importati automaticamente)</div>
                {rec.calendar_only.length === 0 ? <p className="text-muted-foreground text-xs">Nessuno.</p> : rec.calendar_only.map((e) => (
                  <RecRow key={e.event_id}><span>{e.title || "(senza titolo)"} · {e.start}</span><span className="text-xs text-muted-foreground">Solo Calendar</span></RecRow>
                ))}
              </div>
            </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={!!confSid} onOpenChange={(o) => { if (!o) setConfSid(null); }}>
        <DialogContent className="max-w-lg" data-testid="gcal-conflict-modal">
          <DialogHeader>
            <DialogTitle className="font-head">Risoluzione conflitto</DialogTitle>
            <DialogDescription>L'evento è stato modificato sia in K9 Manager sia in Google Calendar. Scegli quale versione mantenere.</DialogDescription>
          </DialogHeader>
          {!confData ? <p className="text-sm text-muted-foreground">Caricamento…</p> : (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-border p-3">
                  <div className="text-xs font-semibold text-muted-foreground mb-1">K9 Manager</div>
                  <ConfField label="Titolo" v={confData.k9.summary} />
                  <ConfField label="Inizio" v={fmtDT(confData.k9.start)} />
                  <ConfField label="Fine" v={fmtDT(confData.k9.end)} />
                  <ConfField label="Luogo" v={confData.k9.location} />
                </div>
                <div className="rounded-lg border border-border p-3">
                  <div className="text-xs font-semibold text-muted-foreground mb-1">Google Calendar</div>
                  <ConfField label="Titolo" v={confData.google.summary} />
                  <ConfField label="Inizio" v={fmtDT(confData.google.start)} />
                  <ConfField label="Fine" v={fmtDT(confData.google.end)} />
                  <ConfField label="Luogo" v={confData.google.location} />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Scegliendo «Usa data/ora/luogo da Calendar» verranno aggiornati <b>solo data, ora, durata e luogo</b> della prestazione. Stato, tipo di servizio, crediti, pacchetto e pagamenti restano invariati.</p>
              <div className="flex gap-2 flex-wrap justify-end">
                <Button size="sm" variant="outline" className="rounded-full" data-testid="gcal-conflict-cancel" onClick={() => setConfSid(null)}>Annulla</Button>
                <Button size="sm" variant="outline" className="rounded-full" data-testid="gcal-conflict-use-calendar" onClick={resolveUseCalendar}>Usa data/ora/luogo da Calendar</Button>
                <Button size="sm" className="rounded-full" data-testid="gcal-conflict-keep-k9" onClick={resolveKeepK9}>Mantieni dati K9 Manager</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
